import { useQuery } from '@apollo/client';
import React, { FC } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import Breadcrumbs from 'Components/Breadcrumbs/Breadcrumbs';
import { useCurrentUser } from 'Components/CurrentUser/CurrentUser';
import PageScroller from 'Components/PageScroller/PageScroller';
import BreadcrumbList from 'Components/SeoSchema/BreadcrumbList';
import PageView from 'common/PageView/PageView';
import { isWindow } from 'common/helpers/helpers';
import config from 'config/config';
import routes from 'utils/routeTranslator';

import RegisterAvailableServices from '../AvailableServices/RegisterAvailableServices';
import Benefits from '../Benefits';
import RegisterCitiesByL3 from '../CitiesByL3/RegisterCitiesByL3';
import GetOrder from '../GetOrder';
import RegisterHero from '../Hero/RegisterHero';
import LatestRequests from '../LatestRequests';
import RegisterOrdersByCities from '../OrdersByCities/OrdersByCities';
import {
  getWeekRequestsText,
  mainPageBreadcrumb,
  redirectAuthenticatedUser,
  defaultPageTitle,
  mainPageBreadcrumbSeo,
} from '../ProviderRegister.helpers';
import RegisterMeta from '../RegisterMeta/RegisterMeta';
import RegisterRelatedOrders from '../RelatedOrders/RegisterRelatedOrders';
import { providerRegisterL3Query, ProviderRegisterL3Response } from './ProviderRegisterL3.helpers';
import './ProviderRegisterL3.scss';

const ProviderRegisterL3: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { currentUser } = useCurrentUser();
  const { data } = useQuery<ProviderRegisterL3Response>(providerRegisterL3Query, {
    variables: {
      slug,
    },
    skip: Boolean(currentUser?.userId),
  });

  // redirect authenticated user to dashboard
  if (isWindow && currentUser && currentUser.userId) {
    return redirectAuthenticatedUser(Boolean(currentUser.isProvider));
  }

  if (!data) return null;

  const breadcrumbsPath = [
    mainPageBreadcrumb,
    {
      name: data.l3BySlug.name,
      url: '',
    },
  ];

  const breadcrumbsSeo = [
    mainPageBreadcrumbSeo,
    {
      name: data.l3BySlug.name,
      url: '',
    },
  ];

  if (!data.l3BySlug.spRegistration) return <Redirect to={routes.get('front::provider.register')} />;

  const { metaDescription, metaTitle, coverUrl, mobileCoverUrl, metaH1 } = data.l3BySlug.spRegistration;
  const pageViewParams: Record<string, string> = {
    'touch-point-page': slug,
  };

  return (
    <main>
      <RegisterMeta
        title={metaTitle || defaultPageTitle}
        description={metaDescription || ''}
        ogPreviewUrl={coverUrl}
        thumbnail={mobileCoverUrl}
        canonicalUrl={config.SITE_URL + routes.get('front::provider.register.l3', { slug })}
      />
      <PageView pageLabel="provider_registration_start" {...pageViewParams} />
      <RegisterHero
        l2CategoryId={data.l3BySlug.l2CategoryId}
        l3CategoryId={data.l3BySlug.id}
        title={metaH1}
        prefixContent={<Breadcrumbs path={breadcrumbsPath} separator="»" />}
        suffixContent={getWeekRequestsText(data.l3BySlug.requestCountSinceLastWeek)}
      />
      <BreadcrumbList items={breadcrumbsSeo} />
      <LatestRequests l3Slug={slug} />
      <GetOrder className="providerRegisterL3__ctaSection" />
      <Benefits />
      <RegisterOrdersByCities />
      <RegisterCitiesByL3 l3Slug={slug} l3Name={data.l3BySlug.name} />
      <RegisterRelatedOrders l3Slug={slug} />
      <RegisterAvailableServices l3Slug={slug} />
      <PageScroller />
    </main>
  );
};

export default ProviderRegisterL3;
